import { campaignLimitsMessage } from '../../domain/taskQueue/campaignLimits';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Campaign, Task } from '../../types';
import {
  abandonTask,
  CampaignStatusCounts,
  claimNextReviewInCampaign,
  claimNextTaskInCampaign,
  claimScopeKey,
  isCampaignMaxClaimedTasksPerUserError,
  isCampaignMaxTasksPerUserError,
  isFirstApprovalRequiredClaimError,
} from '../../services/tasks';
import { useAuth } from '../../contexts/AuthContext';
import { logGlobalError } from '../../utils/globalErrorLogger';
import { categorizeError } from '../../utils/errorCategory';
import { bucketizeQueue, TaskBuckets } from '../../domain/taskQueue/bucketize';
import {
  canClaimCampaign,
  selectVisibleCampaigns,
} from '../../domain/taskQueue/selectors';
import { campaignClaimScopes } from './claimOptions';
import { createQueueReadController, QueueReadController } from '../../features/taskQueue/readController';
import { createCoalescingRunner, type CoalescingRunner } from './countsRefresh';

export type { TaskBuckets } from '../../domain/taskQueue/bucketize';

export interface TaskQueueViewModel {
  campaignLimitsMessage: string | null;
  busy: boolean;
  error: string | null;
  campaigns: Campaign[];
  selectedCampaignId: string | null;
  setSelectedCampaignId: (id: string) => void;
  buckets: TaskBuckets;
  /**
   * Available-task counts per task type for the selected campaign. Empty
   * `taskTypeId` (`""`) is the catch-all key used on legacy untyped
   * campaigns. Refreshed on campaign select and after claim/release events.
   */
  availableCountsByType: Map<string, number>;
  /**
   * Awaiting-review counts per task type for the selected campaign. Derived
   * live from the awaiting-review stream so reviewers see new submissions
   * the moment they land. Empty key on untyped campaigns, like above.
   */
  awaitingReviewCountsByType: Map<string, number>;
  userTaskCountInSelectedCampaign: number;
  userClaimedTaskCountInSelectedCampaign: number;
  /**
   * Whole-campaign per-status task counts for the selected campaign, used by
   * the reviewer/admin progress bar in the review panel. Null for writers
   * (never fetched) and while the first count round-trip is in flight.
   */
  campaignStatusCounts: CampaignStatusCounts | null;
  selectedCampaign: Campaign | null;
  canClaimInSelected: boolean;
  accessNotice: string | null;
  initialize: () => void;
  dispose: () => void;
  releaseTask: (taskId: string) => Promise<void>;
  /**
   * Claim the next available task. Pass `taskTypeId` to scope the claim to
   * one of the campaign's bound task types; omit it (or pass null) to claim
   * any task in the pool — used on single-type or untyped campaigns.
   */
  claimNext: (taskTypeId?: string | null, domainId?: string | null) => Promise<string | null>;
  claimNextReview: (taskTypeId?: string | null, domainId?: string | null) => Promise<string | null>;
}

const EMPTY_BUCKETS: TaskBuckets = {
  myActive: [],
  awaitingReview: [],
  myReviews: [],
  changesRequested: [],
  submitted: [],
  approvedInCampaign: [],
};

const EMPTY_COUNTS: Map<string, number> = new Map();

const STORAGE_KEY = 'workbench.selectedCampaignId';

function readStoredCampaignId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

function writeStoredCampaignId(id: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // QuotaExceededError or SecurityError in private browsing — non-critical.
  }
}

export function useTaskQueueViewModel(): TaskQueueViewModel {
  const { currentUser, isAdmin, reviewCampaignIds, expertId, trustedCampaignIds } = useAuth();
  const [busy, setBusy] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignIdState] = useState<string | null>(readStoredCampaignId);
  const [myOpenTasks, setMyOpenTasks] = useState<Task[]>([]);
  const [myReviews, setMyReviews] = useState<Task[]>([]);
  const [myApproved, setMyApproved] = useState<Task[]>([]);
  const [awaitingReview, setAwaitingReview] = useState<Task[]>([]);
  // Approved tasks across the selected campaign, for the reviewer/admin
  // "Approved" tab. Per-campaign subscription that re-attaches when the
  // selected campaign changes (mirrors awaitingReview). Gated by role —
  // writers don't subscribe to this since they shouldn't see other
  // people's approved work; they have their own subscribeToMyApprovedTasks
  // for the "Submitted" tab.
  const [approvedInCampaign, setApprovedInCampaign] = useState<Task[]>([]);
  // The cross-campaign streams control the main busy flag. Keep a separate
  // readiness key for the selected campaign so its Approved tab and review
  // claim count do not briefly render as zero before their snapshots arrive.
  const [campaignReadsLoadedKey, setCampaignReadsLoadedKey] = useState<string | null>(null);
  const [availableCountsByType, setAvailableCountsByType] = useState<Map<string, number>>(EMPTY_COUNTS);
  // Whole-campaign per-status counts behind the queue progress bars — the
  // full status breakdown for reviewers/admins, the two-segment
  // worked-vs-available view for experts.
  const [campaignStatusCounts, setCampaignStatusCounts] = useState<CampaignStatusCounts | null>(null);
  const [userTaskCountInSelectedCampaign, setUserTaskCountInSelectedCampaign] = useState<number>(0);
  const [userClaimedTaskCountInSelectedCampaign, setUserClaimedTaskCountInSelectedCampaign] = useState<number>(0);
  const [releasedTaskIdsPendingSync, setReleasedTaskIdsPendingSync] = useState<string[]>([]);
  const unsubsRef = useRef<Array<() => void>>([]);
  const campaignSubsRef = useRef<Array<() => void>>([]);
  const readControllerRef = useRef<QueueReadController | null>(null);
  if (!readControllerRef.current) {
    readControllerRef.current = createQueueReadController();
  }
  const readController = readControllerRef.current as QueueReadController;

  const setSelectedCampaignId = useCallback((id: string) => {
    setSelectedCampaignIdState(id);
    writeStoredCampaignId(id);
  }, []);

  const disposeCrossCampaignReads = useCallback(() => {
    unsubsRef.current.forEach((u) => u());
    unsubsRef.current = [];
  }, []);

  const dispose = useCallback(() => {
    disposeCrossCampaignReads();
    campaignSubsRef.current.forEach((u) => u());
    campaignSubsRef.current = [];
    readController.dispose();
  }, [disposeCrossCampaignReads, readController]);

  const initialize = useCallback(() => {
    if (!currentUser) return;
    setBusy(true);
    setError(null);
    // The campaign-specific effect runs before the page calls initialize.
    // Only replace the cross-campaign streams here; disposing the whole
    // controller would tear down the already-attached campaign stream and
    // leave a persisted campaign selection waiting forever.
    disposeCrossCampaignReads();

    let campaignsLoaded = false;
    let openTasksLoaded = false;
    let myReviewsLoaded = false;
    let myApprovedLoaded = false;
    const maybeDone = () => {
      if (campaignsLoaded && openTasksLoaded && myReviewsLoaded && myApprovedLoaded) {
        setBusy(false);
      }
    };

    const unsubscribe = readController.subscribeCrossCampaignReads(currentUser.uid, {
      onCampaigns: (all) => {
        setAllCampaigns(all);
        campaignsLoaded = true;
        maybeDone();
      },
      onMyOpenTasks: (next) => {
        setMyOpenTasks(next);
        openTasksLoaded = true;
        maybeDone();
      },
      onMyReviews: (next) => {
        setMyReviews(next);
        myReviewsLoaded = true;
        maybeDone();
      },
      onMyApprovedTasks: (next) => {
        setMyApproved(next);
        myApprovedLoaded = true;
        maybeDone();
      },
      onError: (err) => {
        setError(err.message);
        setBusy(false);
      },
    });
    unsubsRef.current = [unsubscribe];
  }, [currentUser, disposeCrossCampaignReads, readController]);

  const campaigns = useMemo<Campaign[]>(
    () => selectVisibleCampaigns(allCampaigns, myOpenTasks, { isAdmin, expertId, reviewCampaignIds }),
    [allCampaigns, myOpenTasks, isAdmin, expertId, reviewCampaignIds],
  );

  // Keep the selected campaign id within the visible set. Skip while the
  // list is still loading — an empty `campaigns` array at mount does NOT
  // mean the stored id is invalid; it means subscriptions haven't returned
  // yet. Overwriting to null here would wipe the persisted choice and make
  // the dropdown always snap to the first alphabetical campaign after the
  // real list arrives.
  useEffect(() => {
    if (campaigns.length === 0) return;
    setSelectedCampaignIdState((current) => {
      if (current && campaigns.some((c) => c.id === current)) return current;
      const fallback = campaigns[0]?.id ?? null;
      writeStoredCampaignId(fallback);
      return fallback;
    });
  }, [campaigns]);

  // Per-campaign streams: awaiting-review pool + (for reviewers/admins)
  // the approved pool for the new "Approved" tab. Writers don't see other
  // people's approved tasks, so we skip the approved-in-campaign
  // subscription for them entirely to avoid burning reads.
  //
  // My-active + my-reviews + my-approved + changes-requested all flow
  // through the cross-campaign streams above.
  const canSeeApprovedInCampaign = useMemo(() => {
    if (!selectedCampaignId) return false;
    return isAdmin || reviewCampaignIds.includes(selectedCampaignId);
  }, [isAdmin, reviewCampaignIds, selectedCampaignId]);

  const campaignReadKey = selectedCampaignId
    ? `${selectedCampaignId}:${canSeeApprovedInCampaign ? 'approved' : 'review'}`
    : null;
  const campaignReadsReady = campaignReadKey === null || campaignReadsLoadedKey === campaignReadKey;

  useEffect(() => {
    campaignSubsRef.current.forEach((u) => u());
    campaignSubsRef.current = [];
    setAwaitingReview((prev) => (prev.length === 0 ? prev : []));
    setApprovedInCampaign((prev) => (prev.length === 0 ? prev : []));
    if (!currentUser || !selectedCampaignId) {
      setCampaignReadsLoadedKey(campaignReadKey);
      return;
    }
    let active = true;
    let awaitingLoaded = false;
    let approvedLoaded = !canSeeApprovedInCampaign;
    const markLoaded = () => {
      if (active && awaitingLoaded && approvedLoaded) {
        setCampaignReadsLoadedKey(campaignReadKey);
      }
    };
    const onError = (err: Error) => {
      if (!active) return;
      setError(err.message);
      // Show the error instead of leaving the queue in an indefinite spinner.
      setCampaignReadsLoadedKey(campaignReadKey);
    };
    const unsubscribe = readController.subscribeCampaignReads(
      selectedCampaignId,
      canSeeApprovedInCampaign,
      {
        onAwaitingReview: (next) => {
          if (!active) return;
          awaitingLoaded = true;
          setAwaitingReview(next);
          markLoaded();
        },
        onApprovedInCampaign: (next) => {
          if (!active) return;
          approvedLoaded = true;
          setApprovedInCampaign(next);
          markLoaded();
        },
        onError,
      },
    );
    campaignSubsRef.current = [unsubscribe];
    return () => {
      active = false;
      unsubscribe();
      campaignSubsRef.current = [];
    };
  }, [currentUser, selectedCampaignId, canSeeApprovedInCampaign, campaignReadKey, readController]);

  const buckets = useMemo<TaskBuckets>(() => {
    if (!currentUser || !selectedCampaignId) return EMPTY_BUCKETS;
    return bucketizeQueue({
      myOpenTasks,
      myReviews,
      awaitingReview,
      myApproved,
      approvedInCampaign,
      selectedCampaignId,
      uid: currentUser.uid,
    });
  }, [
    myOpenTasks,
    myReviews,
    awaitingReview,
    myApproved,
    approvedInCampaign,
    currentUser,
    selectedCampaignId,
  ]);

  const myActiveClaims = useMemo<Task[]>(
    () =>
      currentUser
        ? myOpenTasks.filter(
            (task) =>
              task.ownerId === currentUser.uid
              && (task.status === 'claimed' || task.status === 'in_progress'),
          )
        : [],
    [currentUser, myOpenTasks],
  );

  useEffect(() => {
    setReleasedTaskIdsPendingSync((prev) =>
      prev.filter((taskId) => myActiveClaims.some((task) => task.id === taskId)),
    );
  }, [myActiveClaims]);

  useEffect(() => {
    if (!selectedCampaignId) {
      setUserClaimedTaskCountInSelectedCampaign(0);
      return;
    }
    const count = myActiveClaims.filter(
      (task) =>
        !releasedTaskIdsPendingSync.includes(task.id)
        && task.campaignId === selectedCampaignId,
    ).length;
    setUserClaimedTaskCountInSelectedCampaign(count);
  }, [myActiveClaims, releasedTaskIdsPendingSync, selectedCampaignId]);

  const selectedCampaign = useMemo<Campaign | null>(
    () => {
      const campaign = campaigns.find((c) => c.id === selectedCampaignId);
      if (!campaign) return null;
      return trustedCampaignIds?.includes(campaign.id)
        ? { ...campaign, maxTasksPerUser: campaign.maxTasksPerTrustedUser ?? campaign.maxTasksPerUser }
        : campaign;
    },
    [campaigns, selectedCampaignId, trustedCampaignIds],
  );

  // Awaiting-review counts derived live from the stream — keyed by task type
  // (or empty string for untyped campaigns) to match `availableCountsByType`.
  const awaitingReviewCountsByType = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>();
    for (const t of buckets.awaitingReview) {
      const key = t.taskType ? claimScopeKey(t.taskType, t.domainId) : '';
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [buckets.awaitingReview]);

  // Refresh available counts for the selected campaign whenever it changes
  // or the bound task types shift. count() is cheap; running it per-type in
  // parallel keeps the badge accurate without streaming the whole pool.
  const claimScopes = useMemo(
    () => campaignClaimScopes(selectedCampaign?.taskTypes ?? []),
    [selectedCampaign],
  );
  const claimScopesKey = claimScopes.map((scope) => claimScopeKey(scope.taskTypeId, scope.domainId)).join(',');
  const refreshAvailableCounts = useCallback(async () => {
    if (!selectedCampaignId) {
      setAvailableCountsByType(EMPTY_COUNTS);
      return;
    }
    try {
      const counts = await readController.countAvailableTasksByType(selectedCampaignId, claimScopes);
      setAvailableCountsByType(counts);
    } catch (err: any) {
      // Don't blow up the page if a count() query fails — leave the previous
      // counts in place and surface the error. Pure client-side connectivity
      // loss is already shown to the user via setError and self-heals on the
      // next trigger, so skip Cloud Logging noise for network-only failures.
      setError(err?.message ?? 'Failed to load available counts.');
      if (categorizeError(err).type !== 'network') {
        logGlobalError(err, {
          context: 'TaskQueue.refreshAvailableCounts',
          metadata: { campaignId: selectedCampaignId, claimScopes },
        });
      }
    }
    // claimScopes identity changes per render; gate via the joined key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, claimScopesKey, readController]);

  // Campaign-wide status counts for the claim panels' progress bars. Everyone
  // with a selected campaign gets them: reviewers/admins see the full status
  // breakdown, experts see the collapsed worked-vs-available bar.
  const refreshCampaignStatusCounts = useCallback(async () => {
    if (!selectedCampaignId) {
      setCampaignStatusCounts(null);
      return;
    }
    try {
      const counts = await readController.countTasksByStatus(selectedCampaignId);
      setCampaignStatusCounts(counts);
    } catch (err: any) {
      // The bar is informational — hide it rather than erroring the queue.
      // A dropped browser connection self-heals on the next trigger; only log
      // real (non-network) failures so warning trends stay signal-heavy.
      setCampaignStatusCounts(null);
      if (categorizeError(err).type !== 'network') {
        logGlobalError(err, {
          context: 'TaskQueue.refreshCampaignStatusCounts',
          metadata: { campaignId: selectedCampaignId },
        });
      }
    }
  }, [selectedCampaignId, readController]);

  const refreshUserTaskCountInSelectedCampaign = useCallback(async () => {
    if (!selectedCampaignId || !currentUser) {
      setUserTaskCountInSelectedCampaign(0);
      return;
    }
    try {
      const count = await readController.countUserTasksInCampaign(selectedCampaignId, currentUser.uid);
      setUserTaskCountInSelectedCampaign(count);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load your campaign task count.');
      if (categorizeError(err).type !== 'network') {
        logGlobalError(err, {
          context: 'TaskQueue.refreshUserTaskCountInSelectedCampaign',
          metadata: { campaignId: selectedCampaignId },
        });
      }
    }
  }, [currentUser, selectedCampaignId, readController]);

  // ONE counts pass per triggering event (decision D6).
  //
  // The three counts used to refresh from three independent effects AND from
  // explicit calls after claim/release, so a claim fired each of them twice:
  // once eagerly, once again when the stream it had just changed delivered.
  // They now share a single coalesced pass keyed on everything that should
  // cause a recount.
  const refreshAllCounts = useCallback(async () => {
    await Promise.all([
      refreshAvailableCounts(),
      refreshCampaignStatusCounts(),
      refreshUserTaskCountInSelectedCampaign(),
    ]);
  }, [refreshAvailableCounts, refreshCampaignStatusCounts, refreshUserTaskCountInSelectedCampaign]);

  // Bumped by an action that changed the pools without waiting for a stream.
  const [countsRequestToken, setCountsRequestToken] = useState(0);
  const requestCountsRefresh = useCallback(() => setCountsRequestToken((token) => token + 1), []);

  // A pass in flight absorbs any further trigger, then runs exactly once more
  // if anything changed while it ran — see `countsRefresh.ts`, where the rule
  // is unit-tested against a fake.
  const runCountsRef = useRef(refreshAllCounts);
  runCountsRef.current = refreshAllCounts;
  const countsRunnerRef = useRef<CoalescingRunner | null>(null);
  useEffect(() => {
    // Effects restart in StrictMode and during hot reload. Each setup needs
    // a fresh runner: cancel permanently retires the previous one.
    const runner = createCoalescingRunner(() => runCountsRef.current());
    countsRunnerRef.current = runner;
    return () => {
      runner.cancel();
      countsRunnerRef.current = null;
    };
  }, []);

  useEffect(() => {
    void countsRunnerRef.current?.request();
    // `refreshAllCounts` is intentionally out of the dep list: its identity
    // changes whenever any input does, which would re-run this on every
    // render. The triggers below are the complete set that should recount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCampaignId,
    claimScopesKey,
    currentUser?.uid,
    myOpenTasks,
    myApproved,
    awaitingReview.length,
    approvedInCampaign.length,
    countsRequestToken,
  ]);

  // Claiming is gated on: campaign is active AND caller is an active expert
  // (or admin). Reviewers without active-expert status still see review tabs
  // but not the claim queue.
  const canClaimInSelected = useMemo<boolean>(
    () => canClaimCampaign(selectedCampaign, { isAdmin, expertId }),
    [selectedCampaign, isAdmin, expertId],
  );

  const accessNotice = useMemo<string | null>(() => {
    if (!selectedCampaign) return null;
    if (selectedCampaign.archivedAt) {
      return 'This campaign has been archived. You can finish open work but can’t claim new tasks.';
    }
    const isActiveExpert =
      expertId !== null && selectedCampaign.activeExpertIds.includes(expertId);
    if (!isAdmin && !isActiveExpert && !reviewCampaignIds.includes(selectedCampaign.id)) {
      return 'You’re no longer active on this campaign. You can finish open work but can’t claim new tasks.';
    }
    return null;
  }, [selectedCampaign, isAdmin, expertId, reviewCampaignIds]);

  const claimNext = useCallback(
    async (taskTypeId?: string | null, domainId?: string | null) => {
      if (!currentUser) throw new Error('Not signed in.');
      if (!selectedCampaignId) throw new Error('No campaign selected.');
      try {
        const id = await claimNextTaskInCampaign(
          selectedCampaignId,
          currentUser,
          expertId,
          taskTypeId,
          domainId,
          { bypassClaimingPaused: isAdmin || reviewCampaignIds.includes(selectedCampaignId) },
        );
        // Refresh after the claim attempt so the badge reflects the new pool
        // size whether or not we actually got a task. One request; the
        // coordinator collapses it with the stream deliveries it triggers.
        requestCountsRefresh();
        return id;
      } catch (err: any) {
        if (
          isFirstApprovalRequiredClaimError(err)
          || isCampaignMaxTasksPerUserError(err)
          || isCampaignMaxClaimedTasksPerUserError(err)
        ) {
          throw err;
        }
        setError(err?.message ?? 'Failed to claim task.');
        logGlobalError(err, {
          context: 'TaskQueue.claimNext',
          metadata: { campaignId: selectedCampaignId, taskTypeId, domainId },
        });
        throw err;
      }
    },
    [
      currentUser,
      expertId,
      requestCountsRefresh,
      isAdmin,
      reviewCampaignIds,
      selectedCampaignId,
    ],
  );

  const claimNextReview = useCallback(
    async (taskTypeId?: string | null, domainId?: string | null) => {
      if (!currentUser) throw new Error('Not signed in.');
      if (!selectedCampaignId) throw new Error('No campaign selected.');
      try {
        return await claimNextReviewInCampaign(selectedCampaignId, currentUser, taskTypeId, domainId);
      } catch (err: any) {
        setError(err?.message ?? 'Failed to claim review.');
        logGlobalError(err, {
          context: 'TaskQueue.claimNextReview',
          metadata: { campaignId: selectedCampaignId, taskTypeId, domainId },
        });
        throw err;
      }
    },
    [currentUser, selectedCampaignId],
  );

  const releaseTask = useCallback(
    async (taskId: string) => {
      try {
        await abandonTask(taskId);
        setReleasedTaskIdsPendingSync((prev) => (
          prev.includes(taskId) ? prev : [...prev, taskId]
        ));
        // The released task goes back to `available`; refresh the badge.
        requestCountsRefresh();
      } catch (err: any) {
        setError(err?.message ?? 'Failed to release task.');
        logGlobalError(err, {
          context: 'TaskQueue.releaseTask',
          metadata: { taskId },
        });
        throw err;
      }
    },
    [requestCountsRefresh],
  );

  return {
    campaignLimitsMessage: campaignLimitsMessage(
      selectedCampaign,
      !!selectedCampaign && trustedCampaignIds?.includes(selectedCampaign.id) === true,
    ),
    busy: busy || !campaignReadsReady,
    error,
    campaigns,
    selectedCampaignId,
    setSelectedCampaignId,
    buckets,
    availableCountsByType,
    awaitingReviewCountsByType,
    userTaskCountInSelectedCampaign,
    userClaimedTaskCountInSelectedCampaign,
    campaignStatusCounts,
    selectedCampaign,
    canClaimInSelected,
    accessNotice,
    initialize,
    dispose,
    releaseTask,
    claimNext,
    claimNextReview,
  };
}
